import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router";
import { Import } from "iconsax-react";
import { Button, Prompt, Skeleton } from "@/shared/ui";
import { useAppSelector } from "@/shared/hooks";
import { downloadBlob, extractErrorMessage } from "@/shared/utils";
import { useGetEquityChangesQuery, useDownloadEquityChangesMutation } from "../../api";
import { EquityChangesTable, EquityChangesFormHeader } from "./components";

const CURRENT_YEAR = new Date().getFullYear();

interface PromptState {
  title: string;
  text: string;
  variant?: "success" | "error";
}

function resolveYear(raw: string | null): number {
  const parsed = Number(raw);
  if (Number.isInteger(parsed) && parsed >= 2000 && parsed <= 2100) return parsed;
  return CURRENT_YEAR;
}

export default function EquityChangesReportPage() {
  const { t } = useTranslation("FinancialReportPage");
  const [searchParams] = useSearchParams();
  const [prompt, setPrompt] = useState<PromptState | null>(null);

  const year = resolveYear(searchParams.get("year"));

  const userIin = useAppSelector((state) => state.auth.data?.user?.iin ?? null);

  const { data, isLoading, isError } = useGetEquityChangesQuery({ year });
  const [downloadEquityChanges, { isLoading: isDownloading }] = useDownloadEquityChangesMutation();

  async function handleDownload() {
    if (isDownloading) return;
    try {
      const { blob, filename: serverFilename } = await downloadEquityChanges({ year }).unwrap();
      const fallbackFilename = `${t("downloadFilename.equityChanges")}_${year}.xlsx`;
      downloadBlob(blob, serverFilename || fallbackFilename);
    } catch (err: unknown) {
      setPrompt({
        title: t("messages.downloadFailed"),
        text: extractErrorMessage(err, "messages.downloadFailed", t),
        variant: "error",
      });
    }
  }

  return (
    <>
      <title>{t("equityChanges.meta.title")}</title>
      <section className="flex-1">
        <div className="flex items-center justify-between gap-5 pt-1 pb-4 border-b surface-component-stroke mb-5">
          <h1 className="text-display-xs content-base-primary">{t("equityChanges.title")}</h1>
          <Button variant="primary" size="lg" type="button" onClick={handleDownload} disabled={isDownloading || !data}>
            <Import size={20} color="currentColor" />
            {t("equityChanges.modal.downloadButton")}
          </Button>
        </div>

        {isDownloading && (
          <Prompt
            loaderMode={true}
            onClose={() => {}}
            title=""
            text=""
            loaderText={t("messages.loader.waitingForDownload.text")}
            additionalText={t("messages.loader.waitingForDownload.additionalText")}
            namespace="FinancialReportPage"
          />
        )}

        {prompt && (
          <Prompt
            title={prompt.title}
            text={prompt.text}
            variant={prompt.variant || "success"}
            onClose={() => setPrompt(null)}
            namespace="FinancialReportPage"
          />
        )}

        <div className="radius-3xl surface-base-fill p-7">
          {isLoading ? (
            <div className="flex flex-col gap-2">
              <Skeleton width="100%" height={44} />
              {Array.from({ length: 8 }).map(function renderRowSkeleton(_, index) {
                return <Skeleton key={index} width="100%" height={32} />;
              })}
            </div>
          ) : isError || !data ? (
            <p className="text-body-regular-md content-action-negative">{t("equityChanges.error")}</p>
          ) : (
            <>
              <EquityChangesFormHeader reportYear={data.report_year} orgInfo={data.org_info} userIin={userIin} />
              <p className="text-body-regular-xs content-base-low text-right mb-3">{t("equityChanges.modal.inThousands")}</p>
              <div className="overflow-x-auto component-scroll">
                <EquityChangesTable rows={data.rows} />
              </div>
            </>
          )}
        </div>
      </section>
    </>
  );
}
